/**
 * ************************************
 *
 * @module  Activity.saga.js
 * @author  Matt P
 * @date    01/21/2021
 * @description redux saga file for the Activity component
 *
 * ************************************
 */
// ----------------------------------------------------------------------------|
//                                  Imports
// ----------------------------------------------------------------------------|
import { call, put } from 'redux-saga/effects';
import { toast } from 'react-toastify';

import ACTIONS from 'store/actions/actionTypes';

import API from 'API';

import { GENERIC } from 'constants.js';
import { getPartsFromUrl, constructQueryParams } from 'utils/utils';

import { reportAPIError } from 'utils/ErrorHandlingUtils';

// ----------------------------------------------------------------------------|
//                                Utilities
// ----------------------------------------------------------------------------|
/**
 * @description formats data
 *
 * @param {Array} data - Array of Objects
 *
 * @returns {Array}
 */
const formatData = (data = []) => {
  if (typeof data[0] === 'object') {
    return data.map((each) => each.name);
  }
  return data;
};

// ----------------------------------------------------------------------------|
//                            Sagas - Activity
// ----------------------------------------------------------------------------|
/**
 * @description handles uploading an image
 *
 * @param {Object} action - object passed from the redux action
 */
function* uploadImage(action) {
  const { IMAGE_UPLOAD_FAIL } = GENERIC;
  const { postData } = window.axios;
  const { file, fileId, photo_metadata } = action.data;

  try {
    const response = yield call(postData, API.activityImage, file, {
      'Content-Type': 'multipart/form-data',
    });

    if (response.status === 200) {
      yield put({
        type: ACTIONS.ACTIVITY.UPLOAD_IMAGE_SUCCESS,
        data: { ...response.data, fileId, photo_metadata },
      });
    } else {
      toast.error(IMAGE_UPLOAD_FAIL);
      yield put({ type: ACTIONS.ACTIVITY.UPLOAD_IMAGE_FAIL });
    }
  } catch (error) {
    toast.error(IMAGE_UPLOAD_FAIL);
    yield put({ type: ACTIONS.ACTIVITY.UPLOAD_IMAGE_FAIL });
  }
}

/**
 * @description handles updating data
 *
 * @param {Object} action - object passed from the redux action
 * @param {String} id - id of action
 */
function* updatedData(data, id) {
  const { status: toBeStatus } = data;
  const { secondPart: currentTab } = getPartsFromUrl(window.location.pathname);
  const currentStatus = currentTab === 'drafts' ? 'draft' : currentTab;

  if (toBeStatus === currentStatus) {
    yield put({ type: ACTIONS.ACTIVITIES.MOVE_TO_TOP, data: { id, data } });
  } else {
    yield put({ type: ACTIONS.ACTIVITIES.REMOVE_ACTIVITY, data: { id } });
  }
}

/**
 * @description Fetch Activity object using ID
 *
 * @param {Object} action - object passed from the redux action
 */
function* fetchActivity(action) {
  const { getData } = window.axios;
  try {
    const URL = `${API.activityDetail}${action.data.activityId}`;
    const response = yield call(getData, URL);

    if (response.status === 200) {
      yield put({
        type: ACTIONS.ACTIVITY.FETCH_ACTIVITY_SUCCESS,
        data: response.data,
      });
    } else {
      yield put({ type: ACTIONS.ACTIVITY.FETCH_ACTIVITY_FAIL });
    }
  } catch (error) {
    yield put({ type: ACTIONS.ACTIVITY.FETCH_ACTIVITY_FAIL });
  }
}

/**
 * @description saves data to DB
 *
 * @param {Object} action - object passed from the redux action
 */
function* saveData(action) {
  const { ACTIVITY_SAVE_SUCCESS, ACTIVITY_SAVE_FAIL } = GENERIC;
  const { putData } = window.axios;
  const data = { ...action.data };

  // data.neighborhood = formatData(data.neighborhood);

  yield put({
    type: ACTIONS.ACTIVITY.BUTTON_DISBALING,
    data: { isBtnDisabled: true },
  });

  try {
    let response;

    if (action.id) {
      // update
      response = yield call(putData, `${API.activityDetail}${action.id}`, data);
    } else {
      // create
      response = yield call(putData, API.activityList, data);
    }

    if (response.status === 200) {
      yield put({
        type: ACTIONS.ACTIVITY.SAVE_DATA_SUCCESS,
        data: { ...response.data },
      });

      yield updatedData(response.data, action.id);

      action.successCB();
      toast.success(ACTIVITY_SAVE_SUCCESS);
    } else {
      reportAPIError('Activity Save - HTTP Response', response.data);

      yield put({ type: ACTIONS.ACTIVITY.SAVE_DATA_FAIL });

      toast.error(ACTIVITY_SAVE_FAIL);
    }
  } catch (error) {
    reportAPIError('Overall Activity Save Cache', error);

    yield put({ type: ACTIONS.ACTIVITY.SAVE_DATA_FAIL });

    toast.error(ACTIVITY_SAVE_FAIL);
  } finally {
    yield put({
      type: ACTIONS.ACTIVITY.BUTTON_DISBALING,
      data: { isBtnDisabled: false },
    });
  }
}

// ----------------------------------------------------------------------------|
//                                  Export
// ----------------------------------------------------------------------------|
export { uploadImage, fetchActivity, saveData };
